import { useEffect, useState } from 'react';
import { MapStyle, MonsterTokenStyle } from '../api';
import { MapSection, normalizeMapSectionOrder } from './mapSectionOrder';
import { DesktopBlock, normalizeDesktopBlockOrder } from './desktopBlockOrder';

export const DESKTOP_BLOCK_ORDER_STORAGE_KEY = 'arkhamDesktopBlockOrder';
const DESKTOP_BLOCK_ORDER_CHANGED = 'arkhamDesktopBlockOrderChanged';
export function getStoredDesktopBlockOrder(): DesktopBlock[] {
  try { return normalizeDesktopBlockOrder(JSON.parse(localStorage.getItem(DESKTOP_BLOCK_ORDER_STORAGE_KEY) ?? 'null')); }
  catch { return normalizeDesktopBlockOrder(null); }
}
export function setStoredDesktopBlockOrder(value: readonly DesktopBlock[]): void {
  const order = normalizeDesktopBlockOrder(value);
  try { localStorage.setItem(DESKTOP_BLOCK_ORDER_STORAGE_KEY, JSON.stringify(order)); } catch { /* Notify this window even without storage. */ }
  window.dispatchEvent(new CustomEvent(DESKTOP_BLOCK_ORDER_CHANGED, { detail: order }));
}
export function useDesktopBlockOrder(): DesktopBlock[] {
  const [order, setOrder] = useState(getStoredDesktopBlockOrder);
  useEffect(() => {
    const change = (event: Event) => setOrder(normalizeDesktopBlockOrder((event as CustomEvent).detail));
    const storage = (event: StorageEvent) => {
      if (event.key === DESKTOP_BLOCK_ORDER_STORAGE_KEY || event.key === null) setOrder(getStoredDesktopBlockOrder());
    };
    window.addEventListener(DESKTOP_BLOCK_ORDER_CHANGED, change);
    window.addEventListener('storage', storage);
    return () => { window.removeEventListener(DESKTOP_BLOCK_ORDER_CHANGED, change); window.removeEventListener('storage', storage); };
  }, []);
  return order;
}

export const MAP_SECTION_ORDER_STORAGE_KEY = 'arkhamMapSectionOrder';
const MAP_SECTION_ORDER_CHANGED = 'arkhamMapSectionOrderChanged';
export function getStoredMapSectionOrder(): MapSection[] {
  try { return normalizeMapSectionOrder(JSON.parse(localStorage.getItem(MAP_SECTION_ORDER_STORAGE_KEY) ?? 'null')); }
  catch { return normalizeMapSectionOrder(null); }
}
export function setStoredMapSectionOrder(value: readonly MapSection[]): void {
  const order = normalizeMapSectionOrder(value);
  try { localStorage.setItem(MAP_SECTION_ORDER_STORAGE_KEY, JSON.stringify(order)); } catch { /* Notify this window even without storage. */ }
  window.dispatchEvent(new CustomEvent(MAP_SECTION_ORDER_CHANGED, { detail: order }));
}
export function useMapSectionOrder(): MapSection[] {
  const [order, setOrder] = useState(getStoredMapSectionOrder);
  useEffect(() => {
    const change = (event: Event) => setOrder(normalizeMapSectionOrder((event as CustomEvent).detail));
    const storage = (event: StorageEvent) => {
      if (event.key === MAP_SECTION_ORDER_STORAGE_KEY || event.key === null) setOrder(getStoredMapSectionOrder());
    };
    window.addEventListener(MAP_SECTION_ORDER_CHANGED, change);
    window.addEventListener('storage', storage);
    return () => { window.removeEventListener(MAP_SECTION_ORDER_CHANGED, change); window.removeEventListener('storage', storage); };
  }, []);
  return order;
}

export const ENCOUNTER_ICONS_STORAGE_KEY = 'arkhamEncounterIcons';
const ENCOUNTER_ICONS_CHANGED_EVENT = 'arkhamEncounterIconsChanged';

export const getStoredEncounterIcons = (): boolean => {
  try { return window.localStorage.getItem(ENCOUNTER_ICONS_STORAGE_KEY) !== 'false'; }
  catch { return true; }
};

export const setStoredEncounterIcons = (enabled: boolean): void => {
  try { window.localStorage.setItem(ENCOUNTER_ICONS_STORAGE_KEY, String(enabled)); }
  catch { /* Keep the preference usable in this window when storage is unavailable. */ }
  window.dispatchEvent(new CustomEvent(ENCOUNTER_ICONS_CHANGED_EVENT, { detail: enabled }));
};

export function useEncounterIcons(): boolean {
  const [enabled, setEnabled] = useState(getStoredEncounterIcons);
  useEffect(() => {
    const change = (event: Event) => setEnabled((event as CustomEvent<boolean>).detail);
    const storage = (event: StorageEvent) => {
      if (event.key === ENCOUNTER_ICONS_STORAGE_KEY || event.key === null) setEnabled(getStoredEncounterIcons());
    };
    window.addEventListener(ENCOUNTER_ICONS_CHANGED_EVENT, change);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener(ENCOUNTER_ICONS_CHANGED_EVENT, change);
      window.removeEventListener('storage', storage);
    };
  }, []);
  return enabled;
}

export const MONSTER_TOKEN_SCALE_STORAGE_KEY = 'arkhamMonsterTokenScale';
const MONSTER_TOKEN_SCALE_CHANGED_EVENT = 'arkhamMonsterTokenScaleChanged';
const validMonsterTokenScale = (value: number): number =>
  Number.isFinite(value) && value >= .5 && value <= 2 ? value : 1;

export const getStoredMonsterTokenScale = (): number => {
  try {
    return validMonsterTokenScale(Number(window.localStorage.getItem(MONSTER_TOKEN_SCALE_STORAGE_KEY)));
  } catch {
    return 1;
  }
};

export const setStoredMonsterTokenScale = (value: number): void => {
  const scale = validMonsterTokenScale(value);
  try { window.localStorage.setItem(MONSTER_TOKEN_SCALE_STORAGE_KEY, String(scale)); }
  catch { /* The current window can still use the preference until reload. */ }
  window.dispatchEvent(new CustomEvent(MONSTER_TOKEN_SCALE_CHANGED_EVENT, { detail: scale }));
};

export function useMonsterTokenScale(): number {
  const [scale, setScale] = useState(getStoredMonsterTokenScale);
  useEffect(() => {
    const change = (event: Event) => setScale((event as CustomEvent<number>).detail);
    const storage = (event: StorageEvent) => {
      if (event.key === MONSTER_TOKEN_SCALE_STORAGE_KEY || event.key === null) setScale(getStoredMonsterTokenScale());
    };
    window.addEventListener(MONSTER_TOKEN_SCALE_CHANGED_EVENT, change);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener(MONSTER_TOKEN_SCALE_CHANGED_EVENT, change);
      window.removeEventListener('storage', storage);
    };
  }, []);
  return scale;
}

export const MONSTER_TOKEN_STYLE_STORAGE_KEY = 'arkhamMonsterTokenStyle';

const MONSTER_TOKEN_STYLE_CHANGED_EVENT = 'arkhamMonsterTokenStyleChanged';
const DEFAULT_MONSTER_TOKEN_STYLE: MonsterTokenStyle = 'Digital';

const isMonsterTokenStyle = (value: string | null): value is MonsterTokenStyle =>
  value === 'Classic' || value === 'Digital';

export const getStoredMonsterTokenStyle = (): MonsterTokenStyle => {
  try {
    const storedValue = window.localStorage.getItem(MONSTER_TOKEN_STYLE_STORAGE_KEY);
    return isMonsterTokenStyle(storedValue) ? storedValue : DEFAULT_MONSTER_TOKEN_STYLE;
  } catch {
    return DEFAULT_MONSTER_TOKEN_STYLE;
  }
};

export const setStoredMonsterTokenStyle = (style: MonsterTokenStyle): void => {
  try {
    window.localStorage.setItem(MONSTER_TOKEN_STYLE_STORAGE_KEY, style);
    window.dispatchEvent(new CustomEvent(MONSTER_TOKEN_STYLE_CHANGED_EVENT, { detail: style }));
  } catch {
    window.dispatchEvent(new CustomEvent(MONSTER_TOKEN_STYLE_CHANGED_EVENT, { detail: style }));
  }
};

export const useMonsterTokenStyle = (): MonsterTokenStyle => {
  const [style, setStyle] = useState<MonsterTokenStyle>(() => getStoredMonsterTokenStyle());

  useEffect(() => {
    const handleChange = () => setStyle(getStoredMonsterTokenStyle());
    const handleStorage = (event: StorageEvent) => {
      if (event.key === MONSTER_TOKEN_STYLE_STORAGE_KEY) {
        handleChange();
      }
    };

    window.addEventListener(MONSTER_TOKEN_STYLE_CHANGED_EVENT, handleChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(MONSTER_TOKEN_STYLE_CHANGED_EVENT, handleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return style;
};

export const MAP_STYLE_STORAGE_KEY = 'arkhamMapStyle';

const MAP_STYLE_CHANGED_EVENT = 'arkhamMapStyleChanged';
const DEFAULT_MAP_STYLE: MapStyle = 'Reworked';

const isMapStyle = (value: string | null): value is MapStyle =>
  value === 'Classic' || value === 'Reworked';

export const getStoredMapStyle = (): MapStyle => {
  try {
    const storedValue = window.localStorage.getItem(MAP_STYLE_STORAGE_KEY);
    return isMapStyle(storedValue) ? storedValue : DEFAULT_MAP_STYLE;
  } catch {
    return DEFAULT_MAP_STYLE;
  }
};

export const setStoredMapStyle = (style: MapStyle): void => {
  try {
    window.localStorage.setItem(MAP_STYLE_STORAGE_KEY, style);
    window.dispatchEvent(new CustomEvent(MAP_STYLE_CHANGED_EVENT, { detail: style }));
  } catch {
    window.dispatchEvent(new CustomEvent(MAP_STYLE_CHANGED_EVENT, { detail: style }));
  }
};

export const useMapStyle = (): MapStyle => {
  const [style, setStyle] = useState<MapStyle>(() => getStoredMapStyle());

  useEffect(() => {
    const handleChange = () => setStyle(getStoredMapStyle());
    const handleStorage = (event: StorageEvent) => {
      if (event.key === MAP_STYLE_STORAGE_KEY) {
        handleChange();
      }
    };

    window.addEventListener(MAP_STYLE_CHANGED_EVENT, handleChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(MAP_STYLE_CHANGED_EVENT, handleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return style;
};

export const OTHER_WORLDS_BELOW_MAP_STORAGE_KEY = 'arkhamOtherWorldsBelowMap';
const OTHER_WORLDS_BELOW_MAP_CHANGED = 'arkhamOtherWorldsBelowMapChanged';
export const getStoredOtherWorldsBelowMap = (): boolean => {
  try { return localStorage.getItem(OTHER_WORLDS_BELOW_MAP_STORAGE_KEY) === 'true'; }
  catch { return false; }
};
export const setStoredOtherWorldsBelowMap = (enabled: boolean): void => {
  try { localStorage.setItem(OTHER_WORLDS_BELOW_MAP_STORAGE_KEY, String(enabled)); } catch { /* Still notify this window. */ }
  window.dispatchEvent(new Event(OTHER_WORLDS_BELOW_MAP_CHANGED));
};
export function useOtherWorldsBelowMap(): boolean {
  const [enabled, setEnabled] = useState(getStoredOtherWorldsBelowMap);
  useEffect(() => {
    const update = () => setEnabled(getStoredOtherWorldsBelowMap());
    const storage = (event: StorageEvent) => { if (event.key === OTHER_WORLDS_BELOW_MAP_STORAGE_KEY || event.key === null) update(); };
    window.addEventListener(OTHER_WORLDS_BELOW_MAP_CHANGED, update);
    window.addEventListener('storage', storage);
    return () => { window.removeEventListener(OTHER_WORLDS_BELOW_MAP_CHANGED, update); window.removeEventListener('storage', storage); };
  }, []);
  return enabled;
}
